import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  Sort,
} from "@syncfusion/ej2-react-grids";
import { AiFillEdit } from "react-icons/ai";

import Spinner from "../Components/Spinner";
import ToastError from "../Components/ToastError";
import { equipmentModelsService } from "../../Services/equipment.models.service";
import { manufacturerService } from "../../Services/manufacturer.service";

const PAGE_TITLE = "Modelos de Equipamentos";

export default function EquipmentsModels() {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [equipmentModels, setEquipmentModels] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formId, setFormId] = useState("");

  const [formName, setFormName] = useState("");
  const [formManufacturerId, setFormManufacturerId] = useState("");
  const [formType, setFormType] = useState("");
  const [formVirtualIgnition, setFormVirtualIgnition] = useState(false);
  const [formMovementIndev, setFormMovementIndev] = useState(false);
  const [formSleepMode, setFormSleepMode] = useState(false);
  const [formPcNumber, setFormPcNumber] = useState("");
  const [formIsImplemented, setFormIsImplemented] = useState(false);

  useEffect(() => {
    dispatch({ type: "title", value: PAGE_TITLE });
  }, [dispatch]);

  const handleCloseError = () => {
    setShowError(false);
  };

  const clearSuccess = () => {
    setSuccessMessage("");
  };

  const parseFlag = (value) => {
    if (typeof value === "string") {
      const normalized = value.trim().toUpperCase();
      return (
        normalized === "S" ||
        normalized === "Y" ||
        normalized === "TRUE" ||
        normalized === "1"
      );
    }

    return Boolean(value);
  };

  const boolToSN = (value) => (value ? "S" : "N");

  const normalizeEquipmentModel = (response) => ({
    equipmentModelId: Number(
      response?.equipmentModelId ?? response?.EquipmentModelId ?? 0
    ),
    description: response?.description ?? response?.Description ?? "",
    manufacturerId: Number(
      response?.manufacturerId ?? response?.ManufacturerId ?? 0
    ),
    type: response?.type ?? response?.Type ?? "",
    hasVirtualIgnition: parseFlag(
      response?.hasVirtualIgnition ??
        response?.HasVirtualIgnition ??
        response?.isVirtualIgnition ??
        response?.IsVirtualIgnition ??
        false
    ),
    hasImproperMovementDetection: parseFlag(
      response?.hasImproperMovementDetection ??
        response?.HasImproperMovementDetection ??
        response?.movementIndev ??
        response?.MovementIndev ??
        false
    ),
    hasSleepMode: parseFlag(
      response?.hasSleepMode ??
        response?.HasSleepMode ??
        response?.sleepMode ??
        response?.SleepMode ??
        false
    ),
    pc: response?.pc ?? response?.PC ?? response?.PcNumber ?? response?.pcNumber ?? null,
    isImplemented: parseFlag(
      response?.isImplemented ?? response?.IsImplemented ?? false
    ),
  });

  const normalizeManufacturer = (response) => ({
    manufacturerId: Number(
      response?.manufacturerId ?? response?.ManufacturerId ?? 0
    ),
    manufacturerName:
      response?.manufacturerName ?? response?.ManufacturerName ?? "",
  });

  const loadEquipmentModels = async () => {
    clearSuccess();

    try {
      setIsLoading(true);
      const response = await equipmentModelsService.fetchEquipmentModels();

      const normalized = Array.isArray(response)
        ? response
            .map((item) => normalizeEquipmentModel(item))
            .filter((item) => item?.equipmentModelId && item?.description)
        : [];

      setEquipmentModels(normalized);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const loadManufacturers = async () => {
    try {
      const response = await manufacturerService.getById(0);

      const normalized = Array.isArray(response)
        ? response
            .map((item) => normalizeManufacturer(item))
            .filter((item) => item?.manufacturerId && item?.manufacturerName)
        : [];

      setManufacturers(normalized);
    } catch (error) {
      setManufacturers([]);
    }
  };

  useEffect(() => {
    loadManufacturers();
    loadEquipmentModels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedModels = useMemo(
    () =>
      [...equipmentModels].sort(
        (a, b) => a.equipmentModelId - b.equipmentModelId
      ),
    [equipmentModels]
  );

  const manufacturerNameById = (manufacturerId) => {
    const manufacturer = manufacturers.find(
      (item) => Number(item.manufacturerId) === Number(manufacturerId)
    );
    return manufacturer?.manufacturerName || manufacturerId || "-";
  };

  const boolLabel = (value) => (value ? "Sim" : "Não");

  const actionsTemplate = (item) => (
    <div className="d-flex gap-2">
      <AiFillEdit
        style={{ cursor: "pointer", color: "var(--text-primary)", fontSize: "16px" }}
        onClick={() => openEditForm(item)}
        title="Editar"
      />
    </div>
  );

  const openCreateForm = () => {
    clearSuccess();
    setIsEditing(false);
    setFormId("");
    setFormName("");
    setFormManufacturerId("");
    setFormType("");
    setFormVirtualIgnition(false);
    setFormMovementIndev(false);
    setFormSleepMode(false);
    setFormPcNumber("");
    setFormIsImplemented(false);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    const normalizedItem = normalizeEquipmentModel(item);

    clearSuccess();
    setIsEditing(true);
    setFormId(String(normalizedItem.equipmentModelId));
    setFormName(normalizedItem.description || "");
    setFormManufacturerId(String(normalizedItem.manufacturerId || ""));
    setFormType(normalizedItem.type || "");
    setFormVirtualIgnition(normalizedItem.hasVirtualIgnition);
    setFormMovementIndev(normalizedItem.hasImproperMovementDetection);
    setFormSleepMode(normalizedItem.hasSleepMode);
    setFormPcNumber(
      normalizedItem.pc !== null && normalizedItem.pc !== undefined
        ? String(normalizedItem.pc)
        : ""
    );
    setFormIsImplemented(normalizedItem.isImplemented);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setFormId("");
    setFormName("");
    setFormManufacturerId("");
    setFormType("");
    setFormVirtualIgnition(false);
    setFormMovementIndev(false);
    setFormSleepMode(false);
    setFormPcNumber("");
    setFormIsImplemented(false);
  };

  const buildRequestPayload = () => ({
    EquipmentModelName: formName.trim(),
    ManufacturerId: Number(formManufacturerId),
    Type: formType.trim(),
    IsVirtualIgnition: boolToSN(formVirtualIgnition),
    MovementIndev: boolToSN(formMovementIndev),
    SleepMode: boolToSN(formSleepMode),
    PcNumber: formPcNumber === "" ? null : Number(formPcNumber),
    IsImplemented: boolToSN(formIsImplemented),
  });

  const handleCreate = async () => {
    clearSuccess();

    if (!formName.trim() || !formManufacturerId) return;

    try {
      setIsLoading(true);

      const response = await equipmentModelsService.createEquipmentModel(
        buildRequestPayload()
      );

      if (!response) {
        setShowError(true);
        return;
      }

      const createdId =
        response?.equipmentModelId ?? response?.EquipmentModelId ?? response?.id;

      setSuccessMessage(
        `Modelo criado com sucesso${createdId ? ` (ID ${createdId})` : ""}.`
      );

      await loadEquipmentModels();
      closeForm();
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    clearSuccess();

    if (!formId || !formName.trim() || !formManufacturerId) return;

    try {
      setIsLoading(true);

      const response = await equipmentModelsService.updateEquipmentModel(
        Number(formId),
        buildRequestPayload()
      );

      if (!response) {
        setShowError(true);
        return;
      }

      const updatedId =
        response?.equipmentModelId ?? response?.EquipmentModelId ?? formId;

      setSuccessMessage(`Modelo atualizado com sucesso (ID ${updatedId}).`);

      await loadEquipmentModels();
      closeForm();
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <style>
        {`
          .equipment-models-grid .e-gridheader,
          .equipment-models-grid .e-gridcontent,
          .equipment-models-grid .e-headercontent,
          .equipment-models-grid .e-content {
            overflow-x: hidden !important;
          }

          .equipment-models-grid .e-rowcell,
          .equipment-models-grid .e-headercell {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}
      </style>
      {isLoading ? <Spinner /> : null}
      <ToastError show={showError} closeToast={handleCloseError} />

      {!showForm ? (
        <div className="card p-3" style={{ marginBottom: 16 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Modelos de equipamentos</h5>
            <button className="btn btn-success" onClick={openCreateForm}>
              Adicionar
            </button>
          </div>

          <div className="equipment-models-grid">
            <GridComponent
              dataSource={sortedModels}
              allowPaging
              allowFiltering
              allowSorting
              filterSettings={{ type: "Menu" }}
              pageSettings={{ pageSize: 15 }}
              rowHeight={30}
              height={"auto"}
              width="100%"
              style={{ marginTop: 16 }}
              locale="pt-BR"
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="description"
                  headerText="Descrição"
                  width="170"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="manufacturerId"
                  headerText="Fabricante"
                  width="150"
                  textAlign="Left"
                  template={(item) => manufacturerNameById(item?.manufacturerId)}
                />
                <ColumnDirective field="type" headerText="Tipo" width="90" textAlign="Left" />
                <ColumnDirective
                  field="hasVirtualIgnition"
                  headerText="Ignição virtual"
                  width="110"
                  textAlign="Center"
                  template={(item) => boolLabel(item?.hasVirtualIgnition)}
                />
                <ColumnDirective
                  field="hasImproperMovementDetection"
                  headerText="Mov. indevido"
                  width="105"
                  textAlign="Center"
                  template={(item) => boolLabel(item?.hasImproperMovementDetection)}
                />
                <ColumnDirective
                  field="hasSleepMode"
                  headerText="Sleep mode"
                  width="95"
                  textAlign="Center"
                  template={(item) => boolLabel(item?.hasSleepMode)}
                />
                <ColumnDirective field="pc" headerText="PC" width="65" textAlign="Center" />
                <ColumnDirective
                  field="isImplemented"
                  headerText="Implementado"
                  width="105"
                  textAlign="Center"
                  template={(item) => boolLabel(item?.isImplemented)}
                />
                <ColumnDirective
                  headerText="Ações"
                  width="70"
                  template={actionsTemplate}
                  allowFiltering={false}
                  allowSorting={false}
                />
              </ColumnsDirective>
              <Inject services={[Page, Filter, Sort]} />
            </GridComponent>
          </div>
        </div>
      ) : (
        <div className="card p-3" style={{ marginBottom: 16 }}>
          <h5 className="mb-3">{isEditing ? "Editar modelo" : "Adicionar modelo"}</h5>

          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Nome do modelo</label>
              <input
                type="text"
                className="form-control"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Nome do modelo"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Fabricante</label>
              <select
                className="form-select"
                value={formManufacturerId}
                onChange={(e) => setFormManufacturerId(e.target.value)}
              >
                <option value="">Selecione o fabricante</option>
                {manufacturers.map((item) => (
                  <option key={item.manufacturerId} value={item.manufacturerId}>
                    {item.manufacturerName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Tipo</label>
              <input
                type="text"
                className="form-control"
                value={formType}
                onChange={(e) => setFormType(e.target.value.slice(0, 1))}
                placeholder="Tipo do modelo"
                maxLength={1}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Número do PC</label>
              <input
                type="number"
                className="form-control"
                value={formPcNumber}
                onChange={(e) => setFormPcNumber(e.target.value)}
                placeholder="Número do PC"
                min="0"
                step="1"
              />
            </div>

            <div className="col-md-9 d-flex flex-wrap gap-3">
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isVirtualIgnition"
                  checked={formVirtualIgnition}
                  onChange={(e) => setFormVirtualIgnition(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="isVirtualIgnition">
                  Possui Ignição Virtual
                </label>
              </div>

              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="movementIndev"
                  checked={formMovementIndev}
                  onChange={(e) => setFormMovementIndev(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="movementIndev">
                  Possui Movimento Indevido
                </label>
              </div>

              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="sleepMode"
                  checked={formSleepMode}
                  onChange={(e) => setFormSleepMode(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="sleepMode">
                  Possui modo Sleep
                </label>
              </div>

              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isImplemented"
                  checked={formIsImplemented}
                  onChange={(e) => setFormIsImplemented(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="isImplemented">
                  Está implementado
                </label>
              </div>
            </div>

            <div className="col-12 d-flex gap-2">
              <button className="btn btn-secondary" onClick={closeForm}>
                Cancelar
              </button>
              <button
                className={`btn ${isEditing ? "btn-warning" : "btn-success"}`}
                onClick={isEditing ? handleUpdate : handleCreate}
              >
                {isEditing ? "Salvar edição" : "Adicionar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage ? (
        <div className="alert alert-success mt-3" role="alert">
          {successMessage}
        </div>
      ) : null}
    </div>
  );
}
